import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Star, PlayCircle, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { clip } from "@/lib/zoya";
import { fetchQuiz, checkAnswer } from "@/lib/classroom_quiz";

const Option = ({ text, i, picked, answer, onPick }) => {
  const done = answer !== null;
  const state = !done ? "" : i === answer ? "border-emerald-400 bg-emerald-400/15" : i === picked ? "border-[#FF3B3B] bg-[#FF3B3B]/15" : "opacity-60";
  return (
    <button disabled={picked !== null} onClick={() => onPick(i)} data-testid={`quiz-option-${i}`}
      className={`w-full text-left px-4 py-3 rounded-2xl border border-slate-300 hover:border-[#00E5FF] transition-colors flex items-center justify-between gap-3 ${state}`}>
      <span className="text-sm sm:text-base font-semibold">{text}</span>
      {done && i === answer && <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />}
      {done && i === picked && i !== answer && <XCircle className="w-5 h-5 text-[#FF3B3B] shrink-0" />}
    </button>
  );
};

const Result = ({ score, total, onDone }) => {
  const { L } = useLang();
  return (
    <div className="text-center" data-testid="quiz-result">
      <div className="flex justify-center gap-1">{Array.from({ length: total }, (_, i) => <Star key={i} className={`w-8 h-8 ${i < score ? "fill-[#FFC94D] text-[#B45309]" : "text-slate-600"}`} />)}</div>
      <div className="mt-3 text-2xl font-extrabold" data-testid="quiz-score">{score} / {total}</div>
      <p className="mt-1 text-slate-500 text-sm" data-testid="quiz-feedback">{score === total ? L("Perfect! Zoya Ma'am is proud of you!", "शानदार! ज़ोया मैम को आप पर गर्व है!")
        : score * 2 >= total ? L("Well done! Revise the chapter once more and you'll ace it.", "बहुत अच्छे! चैप्टर एक बार और दोहराइए, पूरे नंबर आएँगे।")
        : L("Good try! Rewatch the chapter and try again, practice makes perfect.", "अच्छी कोशिश! चैप्टर फिर से देखिए, अभ्यास से सब आता है।")}</p>
      <button onClick={onDone} data-testid="quiz-continue-btn" className="mt-5 inline-flex items-center gap-2 px-6 h-11 rounded-full bg-[#FF3B3B] hover:bg-[#E63946] text-white font-bold">
        <PlayCircle className="w-5 h-5" />{L("Continue the class", "क्लास आगे बढ़ाएँ")}
      </button>
    </div>
  );
};

export const QuizCheckpoint = ({ slug, teacher, play, onDone }) => {
  const { L } = useLang();
  const [quiz, setQuiz] = useState(null);
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [err, setErr] = useState(false);
  const [practice, setPractice] = useState(null);
  useEffect(() => {
    fetchQuiz(slug).then((qs) => { if (!qs.length) onDone(0); else { setQuiz(qs); play(clip("quiz")); } }).catch(() => setErr(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const q = quiz?.[i];
  const finished = quiz && i >= quiz.length;
  const pick = async (k) => {
    setPicked(k);
    try {
      const r = await checkAnswer(slug, i, k);
      setAnswer(r.answer);
      if (r.correct) setScore((s) => s + 1);
      if (r.done) setScore(r.score);
      if (r.practice) setPractice(r.counted);
      play(clip(r.correct ? "correct" : "wrong"));
    } catch { setPicked(null); setErr(true); }
  };
  const next = () => { setI(i + 1); setPicked(null); setAnswer(null); setErr(false); };
  // Resume the lesson only after Zoya finishes speaking, so her two voices never overlap.
  const done = () => play(clip("done"), () => onDone(practice ?? score));
  return (
    <div className="fixed inset-0 z-50 bg-[#BFD7F5]/70 backdrop-blur-md grid place-items-center p-4" data-testid="quiz-checkpoint">
      <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_40px_120px_-30px_rgba(10,25,47,0.45)] animate-in fade-in zoom-in-95">
        <div className="flex items-center gap-3">
          <img src={teacher} alt="Zoya Ma'am" className="w-12 h-12 rounded-full object-cover ring-2 ring-[#00E5FF]" />
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#007A8A]">{L("Class checkpoint", "क्लास चेकपॉइंट")}</div>
            <div className="font-bold">{L("Zoya Ma'am asks", "ज़ोया मैम पूछ रही हैं")}{quiz && !finished && ` · ${i + 1}/${quiz.length}`}</div>
          </div>
        </div>
        <div className="mt-5">
          {practice !== null && <p className="mb-3 text-xs font-semibold rounded-xl bg-[#FFF8E6] border border-[#FFC94D] text-[#7A4A00] px-3 py-2" data-testid="quiz-practice-note">
            {L(`Practice round: only your first try earns stars (you got ${practice}/3 then).`, `प्रैक्टिस राउंड: स्टार सिर्फ़ पहली कोशिश के मिलते हैं (तब आपके ${practice}/3 आए थे)।`)}</p>}
          {err && <p className="mb-3 text-sm text-[#C81E4B]" data-testid="quiz-error">{L("Couldn't reach the quiz. Check your internet and try again.", "क्विज़ लोड नहीं हुआ। इंटरनेट देखकर फिर कोशिश करें।")}
            {!quiz && <button onClick={() => onDone(0)} className="ml-2 underline font-semibold" data-testid="quiz-skip-btn">{L("Continue class", "क्लास आगे बढ़ाएँ")}</button>}</p>}
          {!quiz ? (!err && <Loader2 className="w-7 h-7 animate-spin text-[#007A8A] mx-auto" data-testid="quiz-loading" />) : finished ? <Result score={score} total={quiz.length} onDone={done} /> : (
            <>
              <h3 className="text-lg sm:text-xl font-extrabold" data-testid="quiz-question">{L(q.q, q.q_hi)}</h3>
              <div className="mt-4 space-y-2">{q.options.map((o, k) => <Option key={k} text={o} i={k} picked={picked} answer={answer} onPick={pick} />)}</div>
              {answer !== null && <button onClick={next} data-testid="quiz-next-btn" className="mt-5 w-full h-11 rounded-full bg-[#00E5FF] text-[#0A192F] font-extrabold">{L("Next", "आगे")}</button>}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
